import { LuCalendarDays, LuChevronRight, LuClock } from 'react-icons/lu';

import type { GuestEventSummary } from '@mds/shared/types/guest-event';

import MediaCard from './media-card';

import { formatEventDate, formatTicket } from '@/lib/format';

/**
 * An event as a card — the one drawing of it on the home rail, the events
 * grid and the "Also coming up" row. The pair to `SpaceCard`, and here for the
 * same reason: filled in by hand at each call site, the three had drifted.
 *
 * Only the card, never its container: drop it into a `CardRail` or a
 * `MediaCardRow` as the section wants.
 */
export default function EventCard({ event }: { event: GuestEventSummary }) {
  const { schedule } = event;
  return (
    <MediaCard
      variant="framed"
      imageInset
      to={`/events/${event.slug}`}
      image={event.coverImage ? { src: event.coverImage, alt: event.title } : undefined}
      // The category, not the date — the date is already on the meta row, and
      // a day badge on the picture would say it twice.
      badge={event.category}
      title={event.title}
      description={event.shortDescription}
      meta={[
        { label: formatEventDate(schedule.date), icon: LuCalendarDays },
        // The API's 24-hour clock times, as sent: `18:00 – 20:00`.
        { label: `${schedule.startTime} – ${schedule.endTime}`, icon: LuClock },
      ]}
      // A full-width strip under the meta row. The cue is a `span`, not a
      // link: the card already carries `to`, and the footer slot hands its
      // clicks down to that link.
      footer={
        // On the phone's list item: no strip, just the price under the
        // facts, and the card's chevron in place of "Details".
        <div className="flex items-center justify-between gap-4 border-t border-hairline px-5 py-4 group-data-list:max-sm:border-t-0 group-data-list:max-sm:px-0 group-data-list:max-sm:pt-2 group-data-list:max-sm:pb-0">
          <span className="text-lg font-bold tracking-tight group-data-list:max-sm:text-md">
            {formatTicket(event.price?.amountMinor ?? 0)}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-text group-data-list:max-sm:hidden">
            Details
            <LuChevronRight aria-hidden="true" className="size-4 shrink-0" />
          </span>
        </div>
      }
    />
  );
}
